import { defineMessage, FormattedMessage, IntlShape } from 'react-intl';

import { PhoneCode } from '@src-common/view/adapters/reactHookForm/PhoneField';

import { TCountryCodes, CountryCode } from '../models/country-code';

type PhoneCodeConfig = Omit<PhoneCode, 'validationRules' | 'countryCode'> & {
  countryCode: TCountryCodes;
  length?: number;
  validate?: (phone: string, intl: IntlShape) => string | boolean | undefined;
};

const thresholdMsg = defineMessage({
  defaultMessage: 'It should have {threshold} characters',
  id: 'GHOOzU',
  description: 'Field validation message',
});

const PHONE_CODES: PhoneCodeConfig[] = [
  {
    title: (
      <FormattedMessage
        defaultMessage="Russia (+7)"
        id="dbw4Qp"
        description="Russia country code title"
      />
    ),
    value: '+7',
    countryCode: CountryCode.Russia,
    length: 10,
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="France (+33)"
        id="ehEu7E"
        description="France country code title"
      />
    ),
    value: '+33',
    countryCode: CountryCode.France,
    length: 9,
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="United Kingdom (+44)"
        id="N6ggfV"
        description="UK country code title"
      />
    ),
    value: '+44',
    countryCode: CountryCode.GreatBritain,
    length: 10,
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="United Arab Emirates (+971)"
        id="global_country_code_uae"
        description="UAE country code title"
      />
    ),
    value: '+971',
    countryCode: CountryCode.ArabEmirates,
    validate: (phone: string, intl: IntlShape) => {
      const isValidLandline = /^4\d{7}$/.test(phone);
      const isValidMobile = /^5\d{8}$/.test(phone);
      const isValid = isValidMobile || isValidLandline;
      const isLandline = phone[0] === '4';
      const isMobile = phone[0] === '5';

      if (isLandline && !isValidLandline) {
        return intl.formatMessage(thresholdMsg, { threshold: '8' });
      }

      if (isMobile && !isValidMobile) {
        return intl.formatMessage(thresholdMsg, { threshold: '9' });
      }

      if (!isValid) {
        return intl.formatMessage(thresholdMsg, { threshold: '8 or 9' });
      }
    },
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="United States of America (+1)"
        id="global_country_code_us"
        description="US country code title"
      />
    ),
    value: '+1',
    countryCode: CountryCode.UnitedStates,
    length: 10,
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="Other country"
        id="5GRTsq"
        description="Other country code title"
      />
    ),
    value: '+',
    countryCode: CountryCode.Unknown,
  },
];

export { PHONE_CODES };
